import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  LayoutChangeEvent,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import FaceDetection from '@react-native-ml-kit/face-detection';
import { GestureDetector, GestureHandlerRootView } from 'react-native-gesture-handler';
import Svg, { G, Path } from 'react-native-svg';
import {
  avatarCropFromViewport,
  avatarViewportForFace,
  clampAvatarViewport,
  IDENTITY_AVATAR_VIEWPORT,
  maxAvatarScale,
  type AvatarCropValues,
  type AvatarViewport,
} from '../avatar/avatarCropRect';
import { AVATAR_SHAPE_PATHS, type AvatarShapeId } from '../avatar/shapeCatalog';
import { AvatarShapeStrip } from './AvatarShapeStrip';
import { useLocale } from '../i18n/LocaleContext';
import { chatListPalette } from '../theme/chatListPalette';
import { useVisualTheme } from '../theme/VisualThemeContext';
import { usePhotoZoomGestures } from '../util/usePhotoZoomGestures';

type Props = {
  uri: string;
  /** Размеры исходника из пикера; пусто — узнаем сами через `Image.getSize`. */
  imageWidth?: number | null;
  imageHeight?: number | null;
  title: string;
  shapeId: AvatarShapeId;
  /** Задан — под холстом появляется полоска форм: форма выбирается живьём на своём снимке. */
  onShapeChange?: (id: AvatarShapeId) => void;
  busy?: boolean;
  onCancel: () => void;
  onApply: (crop: AvatarCropValues) => void;
};

/** Затемнение вокруг маски и её контур лежат поверх снимка, поэтому не зависят от палитры. */
const DIM = 'rgba(2, 6, 23, 0.62)';
const OUTLINE = 'rgba(248, 250, 252, 0.92)';

/** Доля холста под маской: остаётся видно, что снимок продолжается за её краем. */
const MASK_FRACTION = 0.82;
const MAX_CANVAS = 360;

/**
 * Кадрирование аватара: маска неподвижна, двигается снимок.
 *
 * <p><b>Почему не проценты.</b> Раньше кадр задавали пятью чипами «100 / 85 / 70 / 55 / 40 %»
 * и протаскиванием по бейджу 120 × 120 с константой чувствительности. Проценты означали долю
 * короткой стороны, то есть меньшее число давало более крупное лицо — обратное тому, что
 * человек ждёт от слова «масштаб». А снимок не шёл за пальцем: сдвиг на экране равнялся
 * `dx · 0.342 / side`, то есть отставал втрое при `side = 1` и по-разному на каждом шаге зума.
 *
 * <p>Здесь ровно один способ: щипок — приближение, перетаскивание — сдвиг, снимок под пальцем
 * один к одному, двойное касание возвращает кадр целиком. Вся математика живёт в
 * `avatar/avatarCropRect` и покрыта тестами; там же лежит прямое преобразование, которым
 * рисует превью `AvatarBadge` и режет сервер, поэтому «что вижу» и «что сохранилось» — одно и
 * то же по построению, а не по совпадению.
 *
 * <p>Маска — реальная форма аватара из `shapeCatalog`, а не круг: форму человек выбирает тут
 * же, глядя на свой снимок, а не по абстрактным контурам до того, как выбрал фото.
 */
export function AvatarPhotoEditor({
  uri,
  imageWidth,
  imageHeight,
  title,
  shapeId,
  onShapeChange,
  busy = false,
  onCancel,
  onApply,
}: Props): React.ReactElement {
  const { t } = useLocale();
  const { resolvedVisualTheme } = useVisualTheme();
  const pal = useMemo(() => chatListPalette(resolvedVisualTheme), [resolvedVisualTheme]);
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();

  const [boxWidth, setBoxWidth] = useState(0);
  const [intrinsic, setIntrinsic] = useState<{ w: number; h: number } | null>(() =>
    typeof imageWidth === 'number' &&
    imageWidth > 0 &&
    typeof imageHeight === 'number' &&
    imageHeight > 0
      ? { w: Math.round(imageWidth), h: Math.round(imageHeight) }
      : null,
  );
  const [viewport, setViewport] = useState<AvatarViewport>(IDENTITY_AVATAR_VIEWPORT);
  const [sizeFailed, setSizeFailed] = useState(false);
  /** Состояние на момент начала жеста: дельты считаются от него. */
  /**
   * Текущее состояние для колбэков жеста. Через ref, а не через замыкание: иначе объекты
   * `Gesture.*` пересобирались бы на каждый кадр перетаскивания — ровно тогда, когда жест
   * активен и меняться ему нельзя.
   */
  const viewportRef = useRef<AvatarViewport>(IDENTITY_AVATAR_VIEWPORT);
  /** Автокадр по лицу — один раз на снимок, иначе он отменял бы правки человека. */
  const focusedRef = useRef<string | null>(null);

  const canvasSize = useMemo(() => {
    const available = boxWidth > 0 ? boxWidth : windowWidth - 72;
    return Math.max(180, Math.min(available, windowHeight * 0.45, MAX_CANVAS));
  }, [boxWidth, windowHeight, windowWidth]);
  const maskPx = Math.round(canvasSize * MASK_FRACTION);

  useEffect(() => {
    if (intrinsic) return;
    let cancelled = false;
    Image.getSize(
      uri,
      (w, h) => {
        if (!cancelled && w > 0 && h > 0) setIntrinsic({ w: Math.round(w), h: Math.round(h) });
      },
      () => {
        if (!cancelled) setSizeFailed(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [intrinsic, uri]);

  // Стартовый кадр по лицу. Строго best-effort: детектора может не быть (веб, тесты), лицо
  // может не найтись, снимок может оказаться пейзажем — во всех случаях остаётся кадр по центру.
  useEffect(() => {
    if (!intrinsic || focusedRef.current === uri) return;
    focusedRef.current = uri;
    let alive = true;
    void (async () => {
      try {
        const found = await FaceDetection.detect(uri, {});
        if (!alive || !found || found.length === 0) return;
        const biggest = found
          .map((f) => ({
            x: f.frame?.left ?? 0,
            y: f.frame?.top ?? 0,
            width: f.frame?.width ?? 0,
            height: f.frame?.height ?? 0,
          }))
          .sort((a, b) => b.width * b.height - a.width * a.height)[0];
        if (!biggest || biggest.width <= 0) return;
        const vp = avatarViewportForFace(biggest, intrinsic.w, intrinsic.h, maskPx);
        viewportRef.current = vp;
        setViewport(vp);
      } catch {
        /* детектора нет или снимок ему не по зубам — кадр остаётся по центру */
      }
    })();
    return () => {
      alive = false;
    };
  }, [intrinsic, maskPx, uri]);

  const applyViewport = useCallback(
    (next: AvatarViewport) => {
      if (!intrinsic) return;
      const clamped = clampAvatarViewport(next, intrinsic.w, intrinsic.h, maskPx);
      viewportRef.current = clamped;
      setViewport(clamped);
    },
    [intrinsic, maskPx],
  );

  // Маска меняет размер вместе с холстом (поворот экрана), а сдвиг живёт в экранных пикселях —
  // без пересчёта кадр после поворота прыгал бы.
  useEffect(() => {
    if (!intrinsic) return;
    setViewport((prev) => {
      const next = clampAvatarViewport(prev, intrinsic.w, intrinsic.h, maskPx);
      viewportRef.current = next;
      return next;
    });
  }, [intrinsic, maskPx]);

  const onBoxLayout = useCallback((e: LayoutChangeEvent) => {
    setBoxWidth(e.nativeEvent.layout.width);
  }, []);

  // Двойное касание здесь возвращает кадр как был, а не приближает: редактор аватара —
  // про выбор кадра, и «сбросить» тут нужнее, чем «рассмотреть».
  const gesture = usePhotoZoomGestures({
    viewportRef,
    apply: applyViewport,
    onDoubleTap: () => IDENTITY_AVATAR_VIEWPORT,
  });

  const shapePath = AVATAR_SHAPE_PATHS[shapeId];

  /**
   * Затемнение с дырой по форме: рамка холста и сама форма идут одним контуром с `evenodd`,
   * поэтому внутри формы заливки нет. Так не нужен ни `Mask`, ни второй слой — и дыра может
   * быть любой формы, не только круглой.
   */
  const dimPath = useMemo(() => {
    const k = maskPx / 100;
    const margin = (canvasSize - maskPx) / 2;
    const x0 = -margin / k;
    const y0 = -margin / k;
    const x1 = (canvasSize - margin) / k;
    const y1 = (canvasSize - margin) / k;
    return `M ${x0} ${y0} L ${x1} ${y0} L ${x1} ${y1} L ${x0} ${y1} Z ${shapePath}`;
  }, [canvasSize, maskPx, shapePath]);

  const imageStyle = useMemo(() => {
    if (!intrinsic) return null;
    // Экранных пикселей на пиксель исходника: при `scale = 1` короткая сторона ровно закрывает
    // маску. Перенос стоит в списке ДО масштаба и потому применяется после него — то есть
    // `tx`/`ty` остаются экранными, ровно как их отдал жест (см. `PhotoCropEditor`).
    const k = (maskPx / Math.min(intrinsic.w, intrinsic.h)) * viewport.scale;
    return {
      width: intrinsic.w,
      height: intrinsic.h,
      transform: [{ translateX: viewport.tx }, { translateY: viewport.ty }, { scale: k }],
    } as const;
  }, [intrinsic, maskPx, viewport]);

  const canZoom = intrinsic ? maxAvatarScale(intrinsic.w, intrinsic.h) > 1.01 : false;

  const onDone = useCallback(() => {
    if (!intrinsic) return;
    onApply(avatarCropFromViewport(viewport, intrinsic.w, intrinsic.h, maskPx));
  }, [intrinsic, maskPx, onApply, viewport]);

  return (
    <GestureHandlerRootView style={styles.root}>
      <View style={styles.topBar}>
        <Pressable
          style={styles.textBtn}
          onPress={onCancel}
          disabled={busy}
          accessibilityRole="button"
          accessibilityLabel={t('cancel')}>
          <Text style={[styles.textBtnLabel, { color: pal.settingsFieldLabel }]}>{t('cancel')}</Text>
        </Pressable>
        <Text style={[styles.title, { color: pal.settingsSectionTitle }]} accessibilityRole="header">
          {title}
        </Text>
        <Pressable
          style={styles.textBtn}
          onPress={onDone}
          disabled={busy || !intrinsic}
          accessibilityRole="button"
          accessibilityLabel={t('photoCropApply')}>
          {busy ? (
            <ActivityIndicator color={pal.modalPrimary} />
          ) : (
            <Text
              style={[
                styles.textBtnLabel,
                styles.textBtnPrimary,
                { color: intrinsic ? pal.modalPrimary : pal.settingsFieldLabel },
              ]}>
              {t('photoCropApply')}
            </Text>
          )}
        </Pressable>
      </View>

      <View style={styles.canvasBox} onLayout={onBoxLayout}>
        <View style={[styles.canvas, { width: canvasSize, height: canvasSize }]}>
          {imageStyle ? (
            <GestureDetector gesture={gesture}>
              <View style={StyleSheet.absoluteFill}>
                <View style={styles.imageCenter} pointerEvents="none">
                  <Image source={{ uri }} style={imageStyle} resizeMode="stretch" />
                </View>
                <View style={StyleSheet.absoluteFill} pointerEvents="none">
                  <Svg width={canvasSize} height={canvasSize}>
                    <G
                      transform={`translate(${(canvasSize - maskPx) / 2}, ${
                        (canvasSize - maskPx) / 2
                      }) scale(${maskPx / 100})`}>
                      <Path d={dimPath} fill={DIM} fillRule="evenodd" />
                      <Path d={shapePath} fill="none" stroke={OUTLINE} strokeWidth={200 / maskPx} />
                    </G>
                  </Svg>
                </View>
              </View>
            </GestureDetector>
          ) : (
            <View style={styles.imageCenter}>
              {sizeFailed ? (
                <Text style={[styles.hint, { color: pal.dmErr }]}>
                  {t('settingsAvatarUploadFailed')}
                </Text>
              ) : (
                <ActivityIndicator color={pal.settingsFieldLabel} />
              )}
            </View>
          )}
        </View>
      </View>

      <Text style={[styles.hint, { color: pal.settingsFieldLabel }]}>
        {canZoom ? t('avatarEditorHint') : t('avatarEditorHintPanOnly')}
      </Text>

      <AvatarShapeStrip shapeId={shapeId} onShapeChange={onShapeChange} />
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  root: { gap: 8 },
  topBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontSize: 15, fontWeight: '700', flexShrink: 1, textAlign: 'center' },
  textBtn: { minWidth: 76, minHeight: 44, justifyContent: 'center' },
  textBtnLabel: { fontSize: 15 },
  textBtnPrimary: { fontWeight: '700', textAlign: 'right' },
  canvasBox: { alignItems: 'center' },
  canvas: { overflow: 'hidden', borderRadius: 12 },
  imageCenter: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center' },
  hint: { fontSize: 12, textAlign: 'center' },
  label: { fontSize: 13, fontWeight: '600', marginTop: 2 },
  shapeStrip: { gap: 8, paddingVertical: 2 },
  shapeCell: { padding: 4, borderRadius: 10, borderWidth: 1, borderColor: 'transparent' },
});
